import React, { useEffect, useState, useRef } from 'react';
import { CreditCard, CheckCircle2, XCircle, AlertTriangle, Clock, X } from 'lucide-react';
import { IntencionPointEstadoDto } from '../../types/acreditacion.types';
import { acreditacionApi } from '../../services/acreditacionApi';

interface ModalCobroPointProps {
  isOpen: boolean;
  intencion: IntencionPointEstadoDto | null;
  onAprobado: (intencion: IntencionPointEstadoDto) => void;
  onCancelado: () => void;
  onFallbackManual: () => void;
}

export const ModalCobroPoint: React.FC<ModalCobroPointProps> = ({
  isOpen,
  intencion,
  onAprobado,
  onCancelado,
  onFallbackManual
}) => {
  const [estadoActual, setEstadoActual] = useState<string>('ON_TERMINAL');
  const [segundosRestantes, setSegundosRestantes] = useState<number>(120);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [cancelando, setCancelando] = useState<boolean>(false);

  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isOpen || !intencion) {
      setSegundosRestantes(120);
      setErrorMsg(null);
      setCancelando(false);
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      return;
    }

    setEstadoActual(intencion.estado || 'ON_TERMINAL');
    setSegundosRestantes(120);
    setErrorMsg(null);
    setCancelando(false);

    // Countdown cada segundo
    countdownRef.current = setInterval(() => {
      setSegundosRestantes((prev) => {
        if (prev <= 1) {
          if (countdownRef.current) clearInterval(countdownRef.current);
          setEstadoActual('EXPIRED');
          setErrorMsg('La sesión de cobro ha expirado por inactividad.');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    const verificarEstado = async () => {
      try {
        const res = await acreditacionApi.consultarEstadoIntencionPoint(intencion.idIntencionCobro);
        setEstadoActual(res.estado);

        if (res.estado === 'PROCESSED') {
          if (pollingRef.current) clearInterval(pollingRef.current);
          if (countdownRef.current) clearInterval(countdownRef.current);
          setTimeout(() => {
            onAprobado(res);
          }, 300);
        } else if (res.estado === 'ERROR' || res.estado === 'CANCELED' || res.estado === 'ABANDONED') {
          if (pollingRef.current) clearInterval(pollingRef.current);
          if (countdownRef.current) clearInterval(countdownRef.current);
          setErrorMsg(res.mensajeError || `El cobro no pudo completarse (${res.estado}).`);
        }
      } catch (err: any) {
        console.error('Error polling estado point:', err);
      }
    };

    // Polling inmediato y luego cada 2.5 segundos
    verificarEstado();
    pollingRef.current = setInterval(verificarEstado, 2500);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [isOpen, intencion, onAprobado]);

  if (!isOpen || !intencion) return null;

  const handleCancelar = async () => {
    try {
      setCancelando(true);
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      await acreditacionApi.cancelarIntencionPoint(intencion.idIntencionCobro);
    } catch (err) {
      console.warn('Error cancelando intención:', err);
    } finally {
      setCancelando(false);
      onCancelado();
    }
  };

  const formatearTiempo = (seg: number) => {
    const m = Math.floor(seg / 60);
    const s = seg % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in">
      <div className="card-blueprint w-full max-w-lg rounded-3xl p-6 sm:p-8 shadow-2xl border border-brand-cyan/40 bg-brand-dark/95 text-center relative overflow-hidden">
        
        {/* Botón cerrar / cancelar */}
        <button
          onClick={handleCancelar}
          disabled={cancelando || estadoActual === 'PROCESSED'}
          className="absolute top-4 right-4 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors disabled:opacity-30"
          title="Cancelar cobro"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Encabezado del Terminal */}
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-cyan/20 border border-brand-cyan/40 text-brand-sky text-xs font-bold mb-4">
          <CreditCard className="w-3.5 h-3.5" />
          <span>Terminal: {intencion.nombreTerminal} ({intencion.deviceId})</span>
        </div>

        {/* Importe a Cobrar */}
        <div className="mb-6">
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Total a Cobrar</div>
          <div className="text-3xl sm:text-4xl font-black font-heading text-white tracking-tight mt-1">
            ${intencion.monto.toLocaleString('es-AR')}
          </div>
          <div className="text-xs text-brand-cyan/80 mt-1">
            {intencion.cantidadEntradas} {intencion.cantidadEntradas === 1 ? 'entrada' : 'entradas'}
          </div>
        </div>

        {/* Estados Visuales */}
        {estadoActual === 'PROCESSED' ? (
          <div className="space-y-4 py-4 animate-scale-up">
            <div className="w-20 h-20 mx-auto rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center text-emerald-400 shadow-glow-emerald">
              <CheckCircle2 className="w-10 h-10" />
            </div>
            <div>
              <h4 className="text-lg font-bold text-white">¡Pago Aprobado con Éxito!</h4>
              <p className="text-xs text-slate-300 mt-1">
                Comprobante MP: #{intencion.paymentId || 'OK'}
              </p>
            </div>
          </div>
        ) : errorMsg || estadoActual === 'ERROR' || estadoActual === 'CANCELED' || estadoActual === 'EXPIRED' ? (
          <div className="space-y-4 py-3">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-500/20 border-2 border-red-500 flex items-center justify-center text-red-400">
              <XCircle className="w-8 h-8" />
            </div>
            <div>
              <h4 className="text-base font-bold text-red-300">Cobro No Completado</h4>
              <p className="text-xs text-slate-300 mt-1 max-w-xs mx-auto">
                {errorMsg || 'La operación fue rechazada o cancelada en el terminal Point.'}
              </p>
            </div>

            <div className="pt-2 flex flex-col sm:flex-row gap-3 justify-center">
              <button
                type="button"
                onClick={handleCancelar}
                className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold uppercase transition-all"
              >
                Cerrar
              </button>
              <button
                type="button"
                onClick={() => {
                  if (pollingRef.current) clearInterval(pollingRef.current);
                  if (countdownRef.current) clearInterval(countdownRef.current);
                  onFallbackManual();
                }}
                className="px-4 py-2.5 rounded-xl bg-amber-500/20 border border-amber-500/40 hover:bg-amber-500/30 text-amber-300 text-xs font-bold uppercase transition-all flex items-center justify-center gap-1.5"
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                <span>Ingreso Manual de Cupón</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-5 py-2">
            {/* Animación de espera / tarjeta */}
            <div className="relative w-24 h-24 mx-auto flex items-center justify-center">
              <div className="absolute inset-0 rounded-full border-4 border-brand-cyan/20 border-t-brand-cyan animate-spin" />
              <CreditCard className="w-10 h-10 text-brand-cyan animate-pulse" />
            </div>

            <div className="space-y-1">
              <h4 className="text-base font-bold text-white">Acerque o inserte la tarjeta en el terminal</h4>
              <p className="text-xs text-slate-400 max-w-sm mx-auto">
                El importe ya fue enviado a la pantalla de <span className="text-brand-sky font-semibold">{intencion.nombreTerminal}</span>. Esperando confirmación del cliente...
              </p>
            </div>

            {/* Temporizador */}
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800/80 border border-slate-700 text-slate-300 text-xs font-mono">
              <Clock className="w-3.5 h-3.5 text-amber-400" />
              <span>Tiempo restante: {formatearTiempo(segundosRestantes)}</span>
            </div>

            {/* Acciones de cancelación y contingencia */}
            <div className="pt-4 border-t border-slate-700/50 flex flex-col sm:flex-row gap-3 justify-center">
              <button
                type="button"
                disabled={cancelando}
                onClick={handleCancelar}
                className="px-4 py-2.5 rounded-xl bg-red-950/40 border border-red-500/40 hover:bg-red-900/50 text-red-200 text-xs font-bold uppercase transition-all disabled:opacity-50"
              >
                {cancelando ? 'Cancelando...' : 'Cancelar Cobro'}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (pollingRef.current) clearInterval(pollingRef.current);
                  if (countdownRef.current) clearInterval(countdownRef.current);
                  onFallbackManual();
                }}
                className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-bold uppercase transition-all"
              >
                Cambiar a Cupón Manual
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};
